import type { Feel } from "@obean/game-contract";
import { effect } from "@preact/signals";
import { soundOn } from "../host/sound-pref.js";
import { vibrate, volume } from "../settings/settings.js";

/**
 * The feel kit (P5-UX-02, ADR 0048): Obean's own sounds and buzzes, one of each per moment, owned by
 * the platform so every game feels alike. Every sound is synthesized here (Web Audio oscillators),
 * so there are no sound files and nothing is borrowed. Heard only when the sound is on and the page
 * is in front, at the player's volume; the buzz follows the vibration setting.
 */

/** One note: pitch (Hz), when it starts after the first (s), how long it rings (s). */
export interface Note {
  f: number;
  at: number;
  dur: number;
  type: OscillatorType;
  gain: number;
}

const tone = (type: OscillatorType, gain: number, dur: number) => (f: number, at: number) => ({
  f,
  at,
  dur,
  type,
  gain,
});
const soft = tone("sine", 0.22, 0.09);
const bell = tone("triangle", 0.2, 0.22);

/** Each moment's sound and buzz. Short: none rings past half a second but the win, loss and level up. */
export const FEEL: Record<Feel, { notes: readonly Note[]; buzz: number | readonly number[] }> = {
  tap: { notes: [tone("sine", 0.12, 0.05)(880, 0)], buzz: 8 },
  right: { notes: [soft(660, 0), soft(990, 0.08)], buzz: 15 },
  wrong: {
    notes: [tone("triangle", 0.2, 0.12)(247, 0), tone("triangle", 0.2, 0.16)(196, 0.1)],
    buzz: [30, 40, 30],
  },
  found: { notes: [soft(784, 0), soft(1175, 0.07)], buzz: 20 },
  win: {
    notes: [bell(523, 0), bell(659, 0.12), bell(784, 0.24), bell(1046, 0.36)],
    buzz: [30, 50, 30, 50, 60],
  },
  lose: { notes: [bell(440, 0), bell(392, 0.14), bell(349, 0.28), bell(294, 0.42)], buzz: 60 },
  star: { notes: [tone("sine", 0.2, 0.3)(1319, 0), tone("sine", 0.1, 0.3)(1976, 0.04)], buzz: 15 },
  levelUp: {
    notes: [523, 659, 784, 1046, 1319].map((f, i) => bell(f, i * 0.08)),
    buzz: [20, 30, 20, 30, 40],
  },
  unlock: { notes: [soft(392, 0), soft(587, 0.1), soft(784, 0.2)], buzz: [20, 40, 20] },
};

/** Least time between two of the same (ms): a burst of finds is one sound, not ten. */
export const SPACING_MS = 60;

let ac: AudioContext | null = null;
let master: GainNode | null = null;
const last = new Map<Feel, number>();
const silent = () => undefined;

/** Browsers start audio only after a tap or key press on the page, so the context waits for one. */
function context(): AudioContext | null {
  if (typeof AudioContext === "undefined") return null;
  try {
    if (!ac) {
      ac = new AudioContext();
      master = ac.createGain();
      master.connect(ac.destination);
    }
    if (ac.state === "suspended") void ac.resume().catch(silent);
    return ac;
  } catch {
    return null; // no output device: silent, as a browser without Web Audio is
  }
}

/** Whether a sound may be heard now: the switch is on and the page is in front. */
const audible = () =>
  soundOn.peek() && (typeof document === "undefined" || document.visibilityState === "visible");

if (typeof addEventListener === "function" && typeof document !== "undefined") {
  const unlock = () => {
    if (soundOn.peek()) context();
  };
  addEventListener("pointerdown", unlock, { capture: true, passive: true });
  addEventListener("keydown", unlock, { capture: true });
  // Muted or in the background, the context sleeps (a running one keeps a phone's audio awake).
  const follow = (on: boolean) => {
    if (!ac) return;
    if (on && document.visibilityState === "visible") void ac.resume().catch(silent);
    else void ac.suspend().catch(silent);
  };
  document.addEventListener("visibilitychange", () => {
    follow(soundOn.peek());
  });
  effect(() => {
    follow(soundOn.value);
  });
}

export function feel(name: Feel, options: { quiet?: boolean } = {}): void {
  const now = typeof performance === "undefined" ? Date.now() : performance.now();
  if (now - (last.get(name) ?? -Infinity) < SPACING_MS) return;
  last.set(name, now);
  const { notes, buzz } = FEEL[name];
  vibrate(buzz);
  if (options.quiet === true || !audible()) return;
  const ctx = context();
  if (!ctx) return;
  // Asleep since a mute or the background: woken, then played.
  if (ctx.state === "running") play(ctx, notes);
  else
    void ctx.resume().then(() => {
      play(ctx, notes);
    }, silent);
}

function play(ctx: AudioContext, notes: readonly Note[]): void {
  if (!master) return;
  master.gain.value = volume.peek();
  for (const n of notes) {
    const start = ctx.currentTime + n.at;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = n.type;
    osc.frequency.value = n.f;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(n.gain, start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + n.dur);
    osc.connect(gain).connect(master);
    osc.start(start);
    osc.stop(start + n.dur + 0.02);
  }
}
